import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment…');
  const hasProcessed = useRef(false);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    const rewriteSession = searchParams.get('rewrite_session');

    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your email.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true,
      });

        if (response.data.payment_status === 'paid') {
        // Cinematic Trailer purchase → route back to studio project
        const cinematicProject = searchParams.get('cinematic_project');
        if (cinematicProject) {
          setStatus('success');
          setMessage('Trailer unlocked — taking you back to the studio…');
          setTimeout(() => navigate(`/cinematic-studio/${cinematicProject}?paid=1`), 900);
          return;
        }
        // If this was a Rewrite purchase, trigger the rewrite generation now
        if (rewriteSession) {
          setMessage('Payment confirmed. Generating your rewrite — this can take 1–2 minutes…');
          try {
            await axios.post(
              `${API}/rewrite/run/${rewriteSession}`,
              {},
              { withCredentials: true, timeout: 480000 }
            );
          } catch (err) {
            // Best-effort: even if the run call fails, the user can re-open the
            // Rewrite modal which will retry via /api/rewrite/checkout/{id}
            if (process.env.NODE_ENV !== 'production') {
              console.error('Rewrite run after payment failed:', err);
            }
          }
          setStatus('success');
          setMessage('Payment successful and your rewrite is ready in the Rewrite panel.');
          return;
        }
        setStatus('success');
        setMessage('Payment successful. You can now proceed with proofreading.');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      setMessage('Payment is being processed…');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Payment status check error:', error);
      }
      setStatus('error');
      setMessage('Failed to verify payment');
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, pollPaymentStatus]);

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-background px-4" data-testid="payment-success-page">
      <div className="w-full max-w-md p-10 panel-luxe text-center animate-fade-up">
        {status === 'checking' && (
          <>
            <div className="inline-block w-14 h-14 border-2 border-border border-t-gold rounded-full animate-spin mb-6" />
            <h1 className="text-3xl font-serif font-light tracking-tight text-ivory mb-3">Verifying Payment</h1>
            <p className="text-sm text-bone font-sans">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={56} weight="duotone" className="mx-auto mb-6 text-gold" />
            <h1 className="text-3xl font-serif font-light tracking-tight text-ivory mb-3" data-testid="payment-success-title">
              Payment Successful
            </h1>
            <p className="text-sm text-bone font-sans mb-8">{message}</p>
            <Button
              onClick={() => navigate('/')}
              className="rounded-lg font-medium text-sm tracking-wide px-6 py-3 bg-gold text-charcoal hover:bg-gold-dark"
              data-testid="continue-btn"
            >
              Continue to Dashboard
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle size={56} weight="duotone" className="mx-auto mb-6 text-rose-400" />
            <h1 className="text-3xl font-serif font-light tracking-tight text-ivory mb-3" data-testid="payment-error-title">
              Payment Issue
            </h1>
            <p className="text-sm text-bone font-sans mb-8">{message}</p>
            <Button
              onClick={() => navigate('/')}
              className="rounded-lg font-medium text-sm tracking-wide px-6 py-3 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
              data-testid="back-btn"
            >
              Back to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
